import React, { useEffect, useState } from 'react';

// Utils
import {
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  TRAINER_SIGNUP_LINK_KEY,
} from '../../util/types';
import {
  isFieldForCategory,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import { Heading, ExternalLink } from '../../components';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import SectionFixedPricingMaybe from './SectionFixedPricingMaybe';

import instagramIcon from './instagram.svg';
import facebookIcon from './iconFacebook.png';
import youtubeIcon from './iconYoutube.png';

import css from './ListingPage.module.css';

// The social platforms a coach can link to, keyed by the field label left after
// the "(Optional)"/"Handle"/"Channel" cleanup below. Each entry carries the icon
// shown on the public page and the profile URL prefix used when the coach saved
// a bare handle instead of a full link. YouTube handles keep their leading "@"
// (it is part of the URL there); Instagram and Facebook ones do not.
const SOCIAL_PLATFORMS = {
  instagram: { icon: instagramIcon, baseUrl: 'https://www.instagram.com/', stripAt: true },
  facebook: { icon: facebookIcon, baseUrl: 'https://www.facebook.com/', stripAt: true },
  youtube: { icon: youtubeIcon, baseUrl: 'https://www.youtube.com/', stripAt: false },
};

const socialPlatform = heading => SOCIAL_PLATFORMS[(heading || '').trim().toLowerCase()] || null;

// A profile address pasted without the "https://" prefix. Matched against the
// known platform domains only, so a handle that happens to contain a dot
// (Instagram allows them, e.g. "simply.fit") is still treated as a handle.
const BARE_PLATFORM_DOMAIN = /^(www\.)?(instagram\.com|facebook\.com|fb\.com|youtube\.com|youtu\.be)\//i;

// Coaches save either a full profile link or just their handle, so accept both.
const socialProfileUrl = (platform, value) => {
  const trimmed = (value || '').trim();
  if (!platform || !trimmed) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (BARE_PLATFORM_DOMAIN.test(trimmed)) {
    return `https://${trimmed}`;
  }
  const handle = platform.stripAt ? trimmed.replace(/^@+/, '') : trimmed;
  return `${platform.baseUrl}${handle}`;
};

// Resolve the selected option keys of a multi-enum field to their display labels
// so they can be shown as pills.
const optionLabels = field =>
  (field?.selectedOptions || []).map(selected => {
    const match = (field.options || []).find(o => `${o.key}` === `${selected}`);
    return match ? match.label : selected;
  });

/**
 * Renders custom listing fields. The listing page lays out the coach's details in
 * regions, selected via the `slot` prop, so other content (e.g. the action
 * buttons) can be placed between them:
 * - `slot="details"` → the subtitle line + price (shown next to the gallery)
 * - `slot="tags"` → the specialty/coaching pills
 * - `slot="bio"` → the bio card
 * - `slot="links"` → the social platform links
 * When no `slot` is given every section is rendered (used by the cover-photo variant).
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration, slot
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const {
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    description,
    marketplaceCurrency,
    coachFirstName,
    intl,
    slot,
  } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };

  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const specialtyField = propsForCustomFields.find(f => f.key === 'specialty');
  const coachingField = propsForCustomFields.find(f => f.key === 'type_of_coaching');

  // Combine the coach's specialties and coaching types into a single row of pills
  // (no per-group headings), matching the design.
  const tagPills = [...optionLabels(specialtyField), ...optionLabels(coachingField)];

  const remainingFields = propsForCustomFields.filter(
    f =>
      f.key !== 'specialty' &&
      f.key !== 'type_of_coaching' &&
      f.key !== TRAINER_SIGNUP_LINK_KEY
  );

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const showDetails = !slot || slot === 'details';
  const showTags = !slot || slot === 'tags';
  const showBio = !slot || slot === 'bio';
  const showLinks = !slot || slot === 'links';

  // Remaining fields are the social handle links and any other extra fields.
  // They render in their own slot so the page can place them where it wants
  // (in the desktop rail, or in the content flow on smaller screens).
  // Clean up field labels for the public page: the "(Optional)" hint belongs to
  // the edit form, and the client asked to drop the "Handle"/"Channel" words so
  // the social links read simply "Instagram", "Facebook", "YouTube".
  const cleanLinkLabel = label =>
    (label || '')
      .replace(/\s*\(optional\)\s*$/i, '')
      .replace(/\s+(handle|channel)\s*$/i, '')
      .trim();

  const cleanedRemainingFields = remainingFields.map(f => ({
    ...f,
    cleanHeading: cleanLinkLabel(f.heading),
  }));

  const socialLinkFields = cleanedRemainingFields.filter(
    f => f.schemaType === SCHEMA_TYPE_TEXT && !!socialPlatform(f.cleanHeading)
  );
  const otherFields = cleanedRemainingFields.filter(f => !socialLinkFields.includes(f));

  const otherFieldsContent = otherFields.map(customFieldProps => {
    // eslint-disable-next-line no-unused-vars
    const { schemaType, key, heading, cleanHeading, text, ...fieldProps } = customFieldProps;
    return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
      <SectionMultiEnumMaybe key={key} heading={cleanHeading} {...fieldProps} />
    ) : schemaType === SCHEMA_TYPE_TEXT ? (
      <SectionTextMaybe key={key} heading={cleanHeading} text={text} {...fieldProps} />
    ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
      <SectionYoutubeVideoMaybe key={key} heading={cleanHeading} {...fieldProps} />
    ) : null;
  });

  // Client feedback: introduce the social links with "More of "{coachFirstName}":"
  // — falls back to a generic phrase if the first name isn't available, so the
  // heading is never left blank or malformed.
  const socialLinksHeading = socialLinkFields.length
    ? coachFirstName
      ? `More of ${coachFirstName}:`
      : 'More of this coach:'
    : null;

  // P25020-104: the platform names used to be spelled out as word links here,
  // while the same profiles also appeared as icons next to "Leave a review".
  // The icons now live here instead, so each profile is offered in one place.
  const socialLinksContent = socialLinkFields.map(({ key, cleanHeading, text }) => {
    const platform = socialPlatform(cleanHeading);
    const href = socialProfileUrl(platform, text);
    return href ? (
      <ExternalLink
        key={key}
        href={href}
        className={css.socialLinkIcon}
        title={cleanHeading}
        aria-label={cleanHeading}
      >
        <img src={platform.icon} alt={cleanHeading} />
      </ExternalLink>
    ) : null;
  });

  return (
    <>
      {showDetails ? (
        <>
          <SectionDetailsMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} />
          {/* On narrow screens the price moves inline next to the title instead
              (see .titleWithPrice in ListingPageCarousel), so it is hidden here. */}
          <div className={css.desktopPriceBlock}>
            <SectionFixedPricingMaybe
              publicData={publicData}
              marketplaceCurrency={marketplaceCurrency}
              intl={intl}
            />
          </div>
        </>
      ) : null}

      {showTags && tagPills.length > 0 ? (
        <ul className={css.tagPills}>
          {tagPills.map((label, index) => (
            <li key={`${label}-${index}`} className={css.tagPill}>
              {label}
            </li>
          ))}
        </ul>
      ) : null}

      {showBio && description ? (
        <section className={css.card}>
          <Heading as="h2" rootClassName={css.cardHeading}>
            Bio
          </Heading>
          <SectionTextMaybe text={description} showAsIngress />
        </section>
      ) : null}

      {showLinks ? (
        <>
          {otherFieldsContent}
          {socialLinksHeading ? (
            <div className={css.socialLinksGroup}>
              <p className={css.socialLinksHeading}>{socialLinksHeading}</p>
              <div className={css.socialLinkIcons}>{socialLinksContent}</div>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
};

export default CustomListingFields;
